<template>
  <div ref="propertiesContainerRef" class="property-contents-container">
    <statechart-properties
      v-show="show"
      v-if="compProps.model && compProps.model.get('type') === 'Statechart'"
      :model="compProps.model"
      @notify-change="modelChanged"
    />
    <state-properties
      v-show="show"
      v-else-if="compProps.model && compProps.model.get('type') === 'State'"
      v-model:tab="tab"
      :model="compProps.model"
      :component-width="componentWidth"
      @notify-change="modelChanged"
    />
    <transition-properties
      v-show="show"
      v-else-if="compProps.model && compProps.model.get('type') === 'Transition'"
      v-model:tab="tab"
      :model="compProps.model"
      :component-width="componentWidth"
      @notify-change="modelChanged"
    />
    <region-properties
      v-show="show"
      v-else-if="compProps.model && compProps.model.get('type') === 'Region'"
      v-model:tab="tab"
      :model="compProps.model"
      :component-width="componentWidth"
      @notify-change="modelChanged"
    />
    <synchronization-choice-properties
      v-show="show"
      v-else-if="
        compProps.model &&
        (compProps.model.get('type') === 'Choice' || compProps.model.get('type') === 'Synchronization')
      "
      v-model:tab="tab"
      :model="compProps.model"
      :component-width="componentWidth"
      @notify-change="modelChanged"
    />
    <note-properties
      v-show="show"
      v-else-if="compProps.model && compProps.model.get('type') === 'Note'"
      :model="compProps.model"
      @notify-change="modelChanged"
    />
    <final-properties
      v-show="show"
      v-else-if="compProps.model && compProps.model.get('type') === 'Final'"
      :model="compProps.model"
      @notify-change="modelChanged"
    />
    <named-element-properties
      v-show="show"
      v-else-if="isNamedElement()"
      v-model:tab="tab"
      :model="compProps.model"
      :component-width="componentWidth"
      @entry-type-change="setEntryType"
      @notify-change="modelChanged"
    />
    <v-container v-else v-show="show">No properties available for selected element</v-container>
  </div>
</template>

<script setup lang="ts">
import StateProperties from "./StateProperties.vue";
import StatechartProperties from "./StatechartProperties.vue";
import TransitionProperties from "./TransitionProperties.vue";
import RegionProperties from "./RegionProperties.vue";
import NamedElementProperties from "./NamedElementProperties.vue";
import SynchronizationChoiceProperties from "./SynchronizationChoiceProperties.vue";
import NoteProperties from "./NoteProperties.vue";
import FinalProperties from "./FinalProperties.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Entry } from "../mvc/entry";
import { dia } from "@clientio/rappid";

const compProps = withDefaults(
  defineProps<{
    show?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any;
  }>(),
  { show: true }
);

const emit = defineEmits(["notifyChange", "update:titleText"]);

onMounted(() => {
  resizeObserver.value = new ResizeObserver(updateWidth);
  resizeObserver.value.observe(propertiesContainerRef.value);
});

onBeforeUnmount(() => {
  resizeObserver.value.disconnect();
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const tab = ref<any>(null);

const propertiesContainerRef = ref(null);
const componentWidth = ref<number>(0);
const resizeObserver = ref<ResizeObserver>();

const updateWidth = () => {
  if (propertiesContainerRef.value) {
    componentWidth.value = propertiesContainerRef.value.clientWidth;
  }
};

function modelChanged(newModel) {
  emit("notifyChange", newModel);
  // update embedded cells (e.g. for entries, which have their names as a labled node)
  newModel
    .getEmbeddedCells()
    .filter((c) => {
      return c.attributes.type === "NodeLabel";
    })
    .forEach((c) => {
      emit("notifyChange", c);
    });
}

function isNamedElement() {
  return compProps.model && "getName" in compProps.model && "setName" in compProps.model;
}

const titleText = computed((): string => {
  return (compProps.model ? compProps.model.get("type") + " " : "") + "Properties";
});

watch(titleText, (value: string) => {
  emit("update:titleText", value);
});

function setEntryType(type: "Initial" | "DeepHistory" | "ShallowHistory") {
  if (compProps.model instanceof Entry) {
    compProps.model.setEntryKind(type);
    emit("notifyChange", compProps.model);
  }
}
</script>

<style>
.property-contents-container {
  display: flex;
  flex-direction: column;
  overflow: auto;
  min-width: 165px;
  max-width: 500px;
  height: 100%;
}
.property-tab {
  padding: 0px 16px 16px 16px;
  overflow: auto;
  height: 100%;
}
.property-title {
  margin: 0px;
  padding: 10px 0px;
  font-size: 14px;
}

.property-container {
  display: flex;
  flex-wrap: wrap;
  padding: 0px;
  gap: 5px 20px;
}

.prio-item-list {
  border: 1px solid rgba(0, 0, 0, 0.42) !important;
  border-radius: 4px;
  padding: 0px;
  cursor: ns-resize;
}
.prio-item-list.theme--dark {
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}
.prio-item:hover {
  background-color: rgba(0, 0, 0, 0.12);
}
.prio-item:hover.theme--dark {
  background-color: rgba(255, 255, 255, 0.12);
}
.prio-item.bordered {
  border-bottom: 1px solid rgba(0, 0, 0, 0.42);
}
.prio-item.bordered.theme--dark {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

/* for priorities table  */
.property-contents-container .v-table > .v-table__wrapper > table > tbody > tr > td {
  padding: 0 8px 0 2px !important;
  max-width: 100px;
}
</style>
